import rtk from 'rtk';
import { name, nullDoc } from './constants';
import {
  documentCanceled,
  documentOpened,
  documentClosed,
  fetchResource,
  fetchDocument,
} from './actions';

const { createReducer } = rtk(name);

/**
 * -----------------------
 * Initial State
 * -----------------------
 */
const initialState = {};

/**
 * -----------------------
 * Helper functions
 * -----------------------
 */

const findDocumentsByResource = (state, { type, id }) =>
  Object.values(state).filter(({ jsonapi }) =>
    [jsonapi?.data, jsonapi?.included]
      .flat()
      .filter(Boolean)
      .find(
        (resource) => resource.type === type && (!id || resource.id === id),
      ),
  );

const findResources = (state, { type, id }) =>
  Object.values(state)
    .flatMap(({ jsonapi }) => [jsonapi?.data, jsonapi?.included])
    .flat()
    .filter(Boolean)
    .filter(
      (resource) => resource.type === type && (!id || resource.id === id),
    );

/**
 * Ensures a document exists in state before updating.
 */
const ensureDocument = (state, uuid) => {
  if (!state[uuid]) {
    throw Error('Document is not opened');
  }
  return state[uuid];
};

/**
 * -----------------------
 * Reducer
 * -----------------------
 */
export const reducer = createReducer(initialState, {
  /**
   */
  [fetchResource.pending]: (
    state,
    {
      meta: {
        arg: { type, id },
      },
    },
  ) => {
    findDocumentsByResource(state, { type, id }).forEach((document) => {
      document.updates += 1;
    });
  },

  /**
   * When a resource is successfully fetched,
   * update all documents containing that resource.
   */
  [fetchResource.fulfilled]: (state, { payload: { type, id, attributes } }) => {
    findResources(state, { type, id }).forEach((resource) =>
      Object.assign(resource.attributes, attributes),
    );
    findDocumentsByResource(state, { type, id }).forEach((document) => {
      document.updates -= 1;
    });
  },

  /**
   */
  [fetchResource.rejected]: (
    state,
    {
      meta: {
        arg: { type, id },
      },
    },
  ) => {
    findDocumentsByResource(state, { type, id }).forEach((document) => {
      document.updates -= 1;
    });
  },

  /**
   * When a document is canceled, remove it.
   */
  [documentCanceled]: (state, { payload: { uuid } }) => {
    state[uuid].canceled = true;
  },

  [documentOpened]: (state, { payload: { uuid } }) => {
    state[uuid] = { ...nullDoc };
  },
  /**
   * When a document is destroyed, remove it.
   */
  [documentClosed]: (state, { payload: { uuid } }) => {
    delete state[uuid];
  },

  /**
   * When a document request starts.
   */
  [fetchDocument.pending]: (
    state,
    {
      meta: {
        arg: { uuid, request },
      },
    },
  ) => {
    const doc = ensureDocument(state, uuid);
    doc.request = request;
    doc.pending = true;
    doc.error = null;
  },

  /**
   * When a document request succeeds.
   */
  [fetchDocument.fulfilled]: (
    state,
    {
      payload: jsonapi,
      meta: {
        arg: { uuid, request },
      },
    },
  ) => {
    const doc = ensureDocument(state, uuid, request);
    doc.jsonapi = jsonapi;
    doc.pending = false;
    doc.updates = 0;
  },

  /**
   * When a document request fails.
   */
  [fetchDocument.rejected]: (
    state,
    {
      error,
      meta: {
        arg: { uuid, request },
      },
    },
  ) => {
    const doc = ensureDocument(state, uuid, request);
    doc.error = error;
    doc.pending = false;
  },
});
